import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, MessageCircle, ShieldCheck, Wind } from "lucide-react";
import { business } from "@/config/business";
import { WhatsAppButton, QuoteButton } from "@/components/CtaButtons";
import { trackEvent, EVENTS } from "@/lib/tracking";

const HERO_IMG =
  "https://images.unsplash.com/photo-1705579605238-24a90c8799c5?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80";

const HEADLINE_LINES = [
  "Solusi AC, HVAC &",
  "Maintenance Profesional",
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.5]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#0F172A]"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 z-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="Unit HVAC komersial pada atap gedung"
          className="h-[118%] w-full object-cover"
          loading="eager"
        />
      </motion.div>

      {/* Overlays: solid dark blue for legibility (no muddy gradients) */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-[1] bg-[#0F172A]/70"
      />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#0F172A] via-[#0F172A]/60 to-[#0F172A]/20" />
      {/* subtle technical grid */}
      <div
        className="absolute inset-0 z-[1] opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-28 sm:px-6 lg:px-8 lg:pt-32">
        <div className="max-w-3xl">
          {/* Brand lockup */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <span className="block text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              SEJUKARA<span className="text-[#E0A400]">.</span>
            </span>
            <span className="mt-1.5 block text-sm font-semibold text-slate-200 sm:text-base">
              {business.brand.descriptor}
            </span>
            <span className="mt-2 block text-[11px] font-bold uppercase tracking-[0.25em] text-[#E0A400] sm:text-xs">
              {business.brand.tagline}
            </span>
            <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
              {business.brand.byline}
            </span>
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 backdrop-blur-sm"
          >
            <span className="flex h-2 w-2 rounded-full bg-[#E0A400]" />
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-200">
              Layanan Rumah &amp; Bisnis · Makassar &amp; Sekitarnya
            </span>
          </motion.div>

          {/* Kinetic masked headline */}
          <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {HEADLINE_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden py-[0.05em]">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 1 ? "text-[#E0A400]" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          {/* Supporting text */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            Untuk Rumah, Bisnis &amp; Fasilitas Perusahaan.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.72 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <QuoteButton
              label={business.cta.primary}
              source="hero"
              size="lg"
              variant="accent"
              testId="hero-quote-cta"
              className="w-full !bg-[#25D366] !text-white hover:!bg-[#1eb85a] sm:w-auto"
            />
            <WhatsAppButton
              label={business.cta.secondary}
              source="hero"
              size="lg"
              testId="hero-whatsapp-cta"
              className="w-full !bg-transparent !text-white ring-1 ring-inset ring-white/25 hover:!bg-white/10 sm:w-auto"
            />
          </motion.div>

          {/* Meta row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-7"
          >
            <a
              href={`https://wa.me/${business.whatsapp.international}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent(EVENTS.WHATSAPP_CLICK, { source: "hero_number" })}
              data-testid="hero-whatsapp-number"
              className="flex items-center gap-2.5 text-sm text-slate-200 transition-colors hover:text-white"
            >
              <MessageCircle className="h-4 w-4 text-[#25D366]" />
              <span className="font-semibold">WhatsApp {business.whatsapp.display}</span>
            </a>
            <span className="flex items-center gap-2.5 text-sm text-slate-300">
              <MapPin className="h-4 w-4 text-[#25D366]" />
              {business.serviceArea}
            </span>
            <span className="flex items-center gap-2.5 text-sm text-slate-300">
              <ShieldCheck className="h-4 w-4 text-[#25D366]" />
              Layanan rumah, bisnis &amp; fasilitas komersial
            </span>
          </motion.div>
        </div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 lg:block"
      >
        <Wind className="h-5 w-5 animate-pulse text-white/40" />
      </motion.div>
    </section>
  );
};

export default Hero;
